/*
  The empty cart's suggestion rail. What used to sit above it in this file —
  `SnusifyCartPanel`, the dropdown mini cart — went when the cart returned to
  Concept's side drawer and `snusify-cart-panel.liquid` was deleted; nothing
  rendered the element any more.
*/
/*
  The empty cart's suggestion rail, upgraded from the merchant's collections to
  this browser's own recently-viewed products.

  The rail is already on the page when this runs — `snusify-cart-empty` renders
  the merchant fallback on the server, so a first-time shopper and a shopper
  with scripting off both get real rows and there is never a flash of nothing.
  This element only replaces them, and only when there is something better to
  show.

  Concept's `<recently-viewed>` is deliberately not reused: on an empty result
  it removes its own section and dispatches `is-empty` rather than leaving a
  fallback standing, and it re-fetches from scratch every time it is
  constructed — which, in a panel that `cart-items` re-renders after every cart
  write, is once per write.

  Loaded by sections/header.liquid, with the rest of the mini cart's stack.
*/

// Shared across every instance and every re-render of the panel, so opening the
// cart five times in a session costs one request. Keyed by the id query, so a
// newly viewed product still invalidates it.
const snusifyCartSuggestCache = new Map();

class SnusifyCartSuggest extends BaseElement {
  get list() {
    return this.querySelector('[data-suggest-list]');
  }

  get limit() {
    return parseInt(this.getAttribute('data-limit'), 10) || 4;
  }

  connectedCallback() {
    super.connectedCallback();

    // The panel hangs in a closed <details>, so its markup is in the DOM long
    // before anyone looks at it. Waiting for the first open keeps this off the
    // critical path of every page load. On the cart page there is no <details>
    // and the rail is visible immediately, so it loads at once.
    const details = this.closest('details');

    if (details && !details.open) {
      this.on(details, 'toggle', () => {
        if (details.open) this.load();
      }, { once: true });
      return;
    }

    this.load();
  }

  get recentlyViewedQuery() {
    if (!theme.config.hasLocalStorage) return '';

    let stored;
    try {
      stored = window.localStorage.getItem(`${theme.settings.themeName}:recently-viewed`);
    }
    catch (error) {
      // Safari in private mode throws on access, not just on write.
      return '';
    }

    let ids;
    try {
      ids = JSON.parse(stored || '[]');
    }
    catch (error) {
      return '';
    }

    if (!Array.isArray(ids)) return '';

    // The writer in js-variables stores the list reversed, so index 0 is the
    // most recently viewed product.
    return Array.from(new Set(ids))
      .slice(0, this.limit)
      .map((id) => `id:${id}`)
      .join(' OR ');
  }

  async load() {
    if (this.loaded) return;

    const query = this.recentlyViewedQuery;
    if (query === '') return;

    this.loaded = true;

    let html = snusifyCartSuggestCache.get(query);

    if (html === undefined) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        const response = await fetch(this.getAttribute('data-url') + encodeURIComponent(query), {
          signal: this.abortController.signal
        });
        if (!response.ok) return;
        html = await response.text();
      }
      catch (error) {
        if (error.name !== 'AbortError') console.error(error);
        return;
      }

      snusifyCartSuggestCache.set(query, html);
    }

    const source = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('#CartSuggestions [data-suggest-list]');

    // No rows came back — every id has been unpublished or is out of this
    // market. The merchant's fallback is still standing, so leave it alone.
    if (!source || source.children.length === 0) return;

    const list = this.list;
    if (!list) return;

    list.innerHTML = source.innerHTML;

    const label = this.querySelector('[data-suggest-label]');
    const recentLabel = this.getAttribute('data-label');
    if (label && recentLabel) label.textContent = recentLabel;
  }
}
customElements.define('snusify-cart-suggest', SnusifyCartSuggest);
